import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, GiftIcon, MessageCircleIcon } from 'lucide-react';
import { PurchaseOptions } from '../shared/PurchaseOptions';
import { useFeatures } from '../../hooks/useFeatures';
import { canSubscribe } from '../../core/rules/features';
import { boxRange, planBoxes } from '../../core/rules/refillPlan';
import { refillPreview, refillRules } from '../../utils/refill';
import { today } from '../../core/format/dates';
import { policyCopy } from '../../data/policies';
import { capitalize } from '../../core/format/text';
import { MediaView } from '../shared/MediaView';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { unlockedIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import type { Routine } from '../../core/types/catalog';
const primary = btnPrice;

/**
 * Het anker van de pagina: het complete protocol, op blush. Bewust geen protocolkaart in een
 * kader (dan stond het verhaal er twee keer), maar één uitgeschreven blok: het beeld blijft staan
 * (computer), rechts het verhaal, de producten in volgorde van gebruik, wat erbij zit en de
 * cadeaus die je met dit protocol vrijspeelt. Eén knop, met de prijs erin.
 */
export function RoutineAnchor({
  routine
}: {
  routine: Routine;
}) {
  const {
    routineTotals
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const site = useSite();
  const pages = usePages();
  const gifts = unlockedIn(site.giftTiers, site.policy.freeShippingFrom, price);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  const features = useFeatures();
  const canRefill = items.some(p => canSubscribe(features, p));
  const [subscribe, setSubscribe] = useState(false);
  // Slim bijvullen: het kortste ritme waarbij elke doos het minimum haalt, en wat een doos meestal kost.
  const start = today();
  const preview = refillPreview(items.filter(p => canSubscribe(features, p) && !isSoldOut(p)).map(product => ({
    product
  })), site.policy.subscription, start);
  const rules = refillRules(site.policy.subscription);
  const rhythm = preview.suggested;
  const range = rhythm === null ? null : boxRange(planBoxes(preview.itemsFor(rhythm), {
    intervalWeeks: rhythm,
    rules,
    earliest: start,
    count: 6
  }));
  const refillMeta = range ? `Elke ${rhythm} weken een doos met wat op raakt, ${range.min === range.max ? euro(range.min) : `meestal ${euro(range.min)} tot ${euro(range.max)}`}. ${capitalize(policyCopy.subscriptionPerks)}.` : '';
  return <Section tone="blush" labelledBy="anchor-routine" contained={false} data-id="element-8549" data-mp="30l1fvf1ejgc0">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8550" data-mp="2vob5978d9cxz">
        <div className="lg:col-span-5" data-id="element-8551" data-mp="2rmpqthg6oqlo">
          <div className="lg:sticky lg:top-28" data-id="element-8552" data-mp="2ro0xbp10kcgh">
            <MediaView media={{
            image: routine.image || founder.withProducts,
            alt: ''
          }} sizes="(min-width: 1024px) 38vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-8553" data-mp="3oavspwlq14w9" />
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-8554" data-mp="3ekkp6b5y8xpw">
          <p className="text-[14px] text-ink" data-id="element-8555" data-mp="3akioo5byaac7">{fill(pages.routines.anchorEyebrow)}</p>
          <h2 id="anchor-routine" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-8556" data-mp="3aipmp3ca8eaj">
            {routine.name}
          </h2>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ink" data-id="element-8557" data-mp="65sjq0smw8qk">{routine.story}</p>
          {routine.forWho && <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-8558" data-mp="3mtfanqfacj72">Voor wie: {routine.forWho.toLowerCase()}.</p>}

          {/* De volgorde is de boodschap: zo gebruik je het, van reinigen tot bescherming. */}
          <h3 className="mt-10 text-[14px] text-ink" data-id="element-8559" data-mp="3dubq0i0gzvqg">In volgorde van gebruik</h3>
          <ol className="mt-3 divide-y divide-ink/15 border-y border-ink/15" data-id="element-8560" data-mp="3htu4auxsd8kb">
            {items.map((p, i) => <li key={p.id} className="flex items-center gap-4 py-3" data-id="element-8561" data-mp="9g3j9fndmifd" data-mpi={i}>
                <span aria-hidden="true" className="w-5 shrink-0 type-name text-muted tabular-nums" data-id="element-8562" data-mp="eeewm0cvrb2c">
                  {i + 1}
                </span>
                <ProductImage src={p.image} alt="" width={48} height={56} className="h-14 w-12 shrink-0 bg-shell p-1" data-id="element-8563" data-mp="ke3pn8m0i9w" />
                <div className="min-w-0 flex-1" data-id="element-8564" data-mp="sdoagtdm5t35">
                  <p className="text-[12px] text-ink" data-id="element-8565" data-mp="scv07ifg4lg9">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-8566" data-mp="x5xvpw3sa4z3">
                    {p.name}
                  </Link>
                </div>
                <span className={`text-[14px] tabular-nums text-muted ${isSoldOut(p) ? 'line-through' : ''}`} data-id="element-8567" data-mp="2cv5rxzf0bjtk">{euro(p.price)}</span>
              </li>)}
          </ol>

          <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-8568" data-mp="2mmjopzfkq5j0">
            {routine.included.length > 0 && <div data-id="element-8569" data-mp="1ywpe7oiq8t1x">
                <h3 className="text-[14px] text-ink" data-id="element-8570" data-mp="1yw658xb21f01">Kosteloos inbegrepen</h3>
                <ul className="mt-3 space-y-2" data-id="element-8571" data-mp="1ty4l091w79mz">
                  {routine.included.map(inc => <li key={inc} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8572" data-mp="28uvgi78g417b">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8573" data-mp="23tftwaqmdmmt" />
                      {inc}
                    </li>)}
                </ul>
              </div>}
            {gifts.length > 0 && <div data-id="element-8574" data-mp="3mccd5ors47rj">
                <h3 className="text-[14px] text-ink" data-id="element-8575" data-mp="3mcuegztatc6b">{fill(pages.routines.gifts.heading)}</h3>
                <ul className="mt-3 space-y-2" data-id="element-8576" data-mp="3ravxxlumzdib">
                  {gifts.map(g => <li key={g.ruleId} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8577" data-mp="7h3e2fji9r1">
                      <GiftIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-8578" data-mp="54qpb83q03a4" />
                      {g.title}
                    </li>)}
                </ul>
              </div>}
          </div>

          <div className="mt-10 max-w-md" data-id="element-8579" data-mp="3dlc0mvjtr8ge">
            <p className="mb-3 text-[14px] text-muted" data-id="element-8580" data-mp="3djhg3icgticu">
              Gaat {months} mee · dezelfde prijs als los, zonder giswerk
            </p>
            {/* Eenmalig of bijvullen: standaard eenmalig, nooit stiekem. Het ritme volgt hoe lang het protocol meegaat. */}
            {canRefill && !allSoldOut && rhythm !== null && <div className="mb-4" data-id="element-8581" data-mp="2pt4zlj35mdan">
                <PurchaseOptions name={`routine-${routine.id}`} legend={`Eenmalig of bijvullen: ${routine.name}`} price={price} value={subscribe} onChange={setSubscribe} options={[{
              value: false,
              title: 'Eenmalig',
              meta: `Eén keer het complete protocol. ${months} vooruit.`
            }, {
              value: true,
              title: 'Bijvullen wanneer het op is',
              meta: `${refillMeta} Stoppen wanneer je wilt.`
            }]} data-id="element-8582" data-mp="170mzyiswudf8" />
              </div>}
            {allSoldOut ? <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${primary} justify-center`} data-id="element-8583" data-mp="f9p5b96zgir4">
                {shopLabels.soldOutShort} · vraag {founder.firstName} om een alternatief
                <span className="sr-only" data-id="element-8584" data-mp="5a84b52oet1h">{shopLabels.viaWhatsApp}</span>
              </a> : <button type="button" onClick={() => addRoutine(routine.id, subscribe && rhythm !== null ? {
            subscribe: true,
            intervalWeeks: rhythm
          } : undefined)} className={primary} data-id="element-8585" data-mp="dzu863c5q16">
                <span className={btnPriceLabel} data-id="element-8586" data-mp="1f8g1t5760432">
                  Protocol in de mand<span className="sr-only" data-id="element-8587" data-mp="1faaoany34499">: {routine.name}</span>
                </span>
                <span className={btnPriceAmount} data-id="element-8588" data-mp="1k93x432ff733">{euro(price)}</span>
              </button>}
            {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8589" data-mp="1a9d11wy7wk0w">{shopLabels.partlySoldOut(soldOutCount)}</p>}
            {/* Twijfel? Eén rustige regel onder de knop, in plaats van een eigen blok "zo werkt het". */}
            {pages.routines.gifts.note && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8590" data-mp="11e5kh8xhzc71">
                <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-8591" data-mp="3vgnxhvev5a7" />
                {fill(pages.routines.gifts.note)}
                <span className="sr-only" data-id="element-8592" data-mp="hsddzh3pgiq1">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>
        </div>
      </div>
    </Section>;
}